import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Upload,
  Space,
  message,
  Alert,
  Statistic,
  Row,
  Col,
  Tag,
  Typography,
  Divider,
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  ReloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { UploadFile, UploadProps } from 'antd';

const { Title, Text } = Typography;

interface PhoneExportRecord {
  userId: string;
  phoneNumber: string | null;
  status: 'success' | 'not_found' | 'no_phone' | 'error';
  errorMessage?: string;
}

const PhoneNumberExport: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null); // 保存上传的文件对象
  const [records, setRecords] = useState<PhoneExportRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [querying, setQuerying] = useState(false);

  // 模拟用户数据（实际应该从后端API获取）
  const mockUserData: Record<string, string> = {
    '258630': '13800138000',
    '258631': '13800138001',
    '258632': '13800138002',
    '258633': '', // 未绑定手机号
    '258634': '13800138004',
    '258635': '13800138005',
    '100000': '13900139000',
    '100001': '13900139001',
    // '999999' 不存在，用于测试用户ID不存在的情况
    // '123456' 不存在，用于测试用户ID不存在的情况
    // '789012' 不存在，用于测试用户ID不存在的情况
  };

  // 处理文件上传
  const handleUpload: UploadProps['beforeUpload'] = (file) => {
    // 保存文件对象
    setUploadedFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseFile(text);
    };
    reader.onerror = () => {
      message.error('文件读取失败，请检查文件格式');
    };
    // 使用UTF-8编码读取文件
    reader.readAsText(file, 'UTF-8');
    
    // 创建 UploadFile 对象
    const uploadFile: UploadFile = {
      uid: Date.now().toString(),
      name: file.name,
      status: 'done',
      originFileObj: file,
    };
    setFileList([uploadFile]);
    return false; // 阻止自动上传
  };

  // 解析文件内容
  const parseFile = (content: string) => {
    if (!content || content.trim().length === 0) {
      message.error('文件内容为空');
      return;
    }

    const lines = content.split(/\r?\n/).filter(line => line.trim());
    if (lines.length === 0) {
      message.error('文件中没有有效内容');
      return;
    }

    const userIds: string[] = [];
    let skippedHeader = false;

    lines.forEach((line, index) => {
      // 跳过表头（如果第一行是"用户ID"或"userId"等）
      if (index === 0 && (line.toLowerCase().includes('userid') || line.toLowerCase().includes('用户id'))) {
        skippedHeader = true;
        return;
      }
      
      // 支持CSV格式（逗号分隔）和纯文本格式（每行一个ID）
      const parts = line.split(',').map(p => p.trim());
      const userId = parts[0] || line.trim();
      
      if (userId && /^\d+$/.test(userId)) {
        userIds.push(userId);
      }
    });

    if (userIds.length === 0) {
      message.error('文件中没有找到有效的用户ID（用户ID必须是纯数字）');
      return;
    }

    message.success(`成功解析 ${userIds.length} 个用户ID`);
    setRecords([]); // 清空之前的结果
  };

  // 查询手机号码
  const handleQuery = async () => {
    if (fileList.length === 0 || !uploadedFile) {
      message.warning('请先上传包含用户ID的文件');
      return;
    }

    setQuerying(true);
    try {
      // 使用保存的文件对象读取文件
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve(e.target?.result as string);
        };
        reader.onerror = () => {
          reject(new Error('文件读取失败'));
        };
        reader.readAsText(uploadedFile, 'UTF-8');
      });
      
      const lines = text.split(/\r?\n/).filter(line => line.trim());
      const userIds: string[] = [];

      lines.forEach((line, index) => {
        if (index === 0 && (line.toLowerCase().includes('userid') || line.toLowerCase().includes('用户id'))) {
          return;
        }
        const parts = line.split(',').map(p => p.trim());
        const userId = parts[0] || line.trim();
        if (userId && /^\d+$/.test(userId)) {
          userIds.push(userId);
        }
      });

      if (userIds.length === 0) {
        message.error('文件中没有找到有效的用户ID');
        return;
      }

      // 模拟API调用（实际应该调用后端API）
      await new Promise(resolve => setTimeout(resolve, 1000));

      const results: PhoneExportRecord[] = userIds.map(userId => {
        // 模拟查询逻辑
        if (!mockUserData.hasOwnProperty(userId)) {
          return {
            userId,
            phoneNumber: null,
            status: 'not_found',
            errorMessage: '用户ID不存在',
          };
        }

        const phone = mockUserData[userId];
        if (!phone || phone === '') {
          return {
            userId,
            phoneNumber: null,
            status: 'no_phone',
            errorMessage: '用户未绑定手机号',
          };
        }

        return {
          userId,
          phoneNumber: phone,
          status: 'success',
        };
      });

      setRecords(results);
      message.success(`查询完成，共处理 ${userIds.length} 个用户ID`);
    } catch (error) {
      message.error('查询失败，请重试');
      console.error('Query error:', error);
    } finally {
      setQuerying(false);
    }
  };

  // 导出结果
  const handleExport = () => {
    if (records.length === 0) {
      message.warning('没有可导出的数据，请先查询');
      return;
    }

    // 生成CSV内容
    const csvHeader = '用户ID,手机号码,状态,错误信息\n';
    const csvRows = records.map(record => {
      const statusMap: Record<string, string> = {
        success: '成功',
        not_found: '用户ID不存在',
        no_phone: '未绑定手机号',
        error: '查询错误',
      };
      return `${record.userId},${record.phoneNumber || ''},${statusMap[record.status]},${record.errorMessage || ''}`;
    });
    const csvContent = csvHeader + csvRows.join('\n');

    // 创建下载链接
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `手机号导出_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    message.success('导出成功');
  };

  // 清空数据
  const handleClear = () => {
    setFileList([]);
    setUploadedFile(null);
    setRecords([]);
    message.info('已清空数据');
  };

  // 统计信息
  const successCount = records.filter(r => r.status === 'success').length;
  const notFoundCount = records.filter(r => r.status === 'not_found').length;
  const noPhoneCount = records.filter(r => r.status === 'no_phone').length;
  const errorCount = records.filter(r => r.status === 'error').length;

  // 表格列定义
  const columns = [
    {
      title: '用户ID',
      dataIndex: 'userId',
      key: 'userId',
      width: 150,
    },
    {
      title: '手机号码',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 150,
      render: (phone: string | null) => phone || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: string) => {
        const statusConfig: Record<string, { color: string; text: string }> = {
          success: { color: 'success', text: '成功' },
          not_found: { color: 'error', text: '用户ID不存在' },
          no_phone: { color: 'warning', text: '未绑定手机号' },
          error: { color: 'error', text: '查询错误' },
        };
        const config = statusConfig[status] || { color: 'default', text: '未知' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '错误信息',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (msg: string | undefined) => msg || '-',
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Title level={4}>手机号码导出</Title>
        <Text type="secondary">
          导入用户ID列表，系统将根据用户ID查询对应的手机号码。支持CSV、TXT格式文件。
        </Text>

        <Divider />

        {/* 使用说明 */}
        <Alert
          message="使用说明"
          description={
            <div>
              <p>1. 上传包含用户ID的文件（CSV或TXT格式）</p>
              <p>2. 文件格式：每行一个用户ID，或CSV格式（第一列为用户ID）</p>
              <p>3. 点击"查询手机号"按钮，系统将根据用户ID查询对应的手机号码</p>
              <p>4. 查询完成后，可以查看结果并导出为CSV文件</p>
              <p style={{ color: '#ff4d4f', marginTop: 8 }}>
                <strong>注意：</strong>导出的数据不包含敏感信息，仅包含用户ID和对应的手机号码。
                如果用户ID不存在或未绑定手机号，将在结果中标记。
              </p>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        {/* 文件上传区域 */}
        <Card size="small" title="步骤1：上传用户ID文件" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Upload
              fileList={fileList}
              beforeUpload={handleUpload}
              onRemove={() => {
                setFileList([]);
                setUploadedFile(null);
                setRecords([]);
                return true;
              }}
              maxCount={1}
              accept=".csv,.txt"
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 12 }}>
              支持格式：CSV、TXT。文件大小不超过10MB。
            </Text>
            {fileList.length > 0 && (
              <div>
                <Text>已选择文件：</Text>
                <Text strong>{fileList[0].name}</Text>
              </div>
            )}
          </Space>
        </Card>

        {/* 查询按钮 */}
        <Card size="small" title="步骤2：查询手机号码" style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<FileTextOutlined />}
              onClick={handleQuery}
              loading={querying}
              disabled={fileList.length === 0}
            >
              查询手机号
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleClear}>
              清空数据
            </Button>
          </Space>
        </Card>

        {/* 统计信息 */}
        {records.length > 0 && (
          <Card size="small" title="查询统计" style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="总记录数"
                  value={records.length}
                  prefix={<FileTextOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="查询成功"
                  value={successCount}
                  valueStyle={{ color: '#3f8600' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="用户ID不存在"
                  value={notFoundCount}
                  valueStyle={{ color: '#cf1322' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="未绑定手机号"
                  value={noPhoneCount}
                  valueStyle={{ color: '#faad14' }}
                  prefix={<ExclamationCircleOutlined />}
                />
              </Col>
            </Row>
          </Card>
        )}

        {/* 结果表格 */}
        {records.length > 0 && (
          <Card
            size="small"
            title="查询结果"
            extra={
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
              >
                导出结果
              </Button>
            }
          >
            <Table
              columns={columns}
              dataSource={records.map((record, index) => ({ ...record, key: index }))}
              pagination={{
                pageSize: 20,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条记录`,
              }}
              scroll={{ y: 400 }}
            />
          </Card>
        )}

        {/* 空状态提示 */}
        {records.length === 0 && fileList.length === 0 && (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <FileTextOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
              <p style={{ marginTop: 16, color: '#8c8c8c' }}>
                请上传包含用户ID的文件开始查询
              </p>
            </div>
          </Card>
        )}
      </Card>
    </div>
  );
};

export default PhoneNumberExport;

